import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActivityFeedEntryDto,
  ActivityFeedEntityLinkDto,
  EntityTypeEnum
} from 'api/activity-feed';
import {
  useActivityFeed,
  ActivityFeedSearchFilters
} from '../hooks/useActivityFeed';
import {
  extractNameFromMessage,
  getActivityInitials,
  getInitials,
  formatRelativeTime,
  getStatusBadge,
  getAvatarColor
} from '../mocks/activityFeedMockData';
import {
  parseMessage,
  getEntityUrl,
  getEntityLabel,
  ParsedMessagePart
} from '../utils/messageParser';
import { useRoleCan } from 'core/hooks/useRoleCan';
import { Capability } from 'core/roles';
import { ActivitySegments } from './ActivitySegments';
import { ActivityContextChips } from './ActivityContextChips';
import { useActivityRowUrl } from '../hooks/useActivityRowUrl';

/** Couleur de chip par code de catégorie (classes en dur : purge Tailwind). */
const CATEGORY_CHIP: Record<string, string> = {
  simulation: 'bg-orange-100 text-orange-700',
  client: 'bg-blue-100 text-blue-700',
  risque: 'bg-teal-100 text-teal-700',
  devis: 'bg-violet-100 text-violet-700',
  contrat: 'bg-emerald-100 text-emerald-700',
  paiement: 'bg-cyan-100 text-cyan-700',
  document: 'bg-slate-100 text-slate-600',
  note: 'bg-amber-100 text-amber-700',
  autre: 'bg-gray-100 text-gray-500'
};

// ============================================
// FILTER PILLS
// ============================================

interface FilterPillProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const FilterPill: React.FC<FilterPillProps> = ({ label, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        h-8 px-3.5 rounded-full text-sm font-medium
        transition-all duration-200
        ${
          isActive
            ? 'bg-slate-700 text-white'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-600'
        }
      `}
    >
      {label}
    </button>
  );
};

// ============================================
// ENTITY LINK COMPONENT
// ============================================

interface EntityLinkProps {
  entityType: EntityTypeEnum;
  entityId: string;
  displayText: string;
  riskId?: string;
  entities?: ActivityFeedEntityLinkDto[];
}

const EntityLink: React.FC<EntityLinkProps> = ({
  entityType,
  entityId,
  displayText,
  riskId,
  entities
}) => {
  const navigate = useNavigate();
  // Liens du flux d'activité : matrice centrale (Courtier uniquement).
  const canFollowLinks = useRoleCan(Capability.ActivityFeedLinks);
  const url = canFollowLinks ? getEntityUrl(entityType, entityId, riskId, entities) : null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (url) {
      navigate(url);
    }
  };

  if (!url) {
    return <span className="text-gray-700">{displayText}</span>;
  }

  return (
    <button
      onClick={handleClick}
      className="text-primary hover:underline font-medium"
    >
      {displayText}
    </button>
  );
};

// ============================================
// ACTIVITY ITEM
// ============================================

interface ActivityItemProps {
  entry: ActivityFeedEntryDto;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ entry }) => {
  const navigate = useNavigate();
  // Clic sur la ligne : demande de validation (valideur) / contractualisation du devis,
  // sinon dossier (via context) -> note -> fiche client, gaté par la matrice.
  const rowUrl = useActivityRowUrl(entry);

  const name = extractNameFromMessage(entry.message, entry.title);
  // Avatar : initiales du souscripteur (context.clientName) quand on les a — cohérent
  // d'une ligne à l'autre. Repli : parsing heuristique du message.
  const initials = entry.context?.clientName
    ? getInitials(entry.context.clientName)
    : getActivityInitials(entry.message, entry.title);
  const avatarColor = getAvatarColor(initials);
  const badge = getStatusBadge(entry);
  const relativeTime = formatRelativeTime(entry.createdAt);

  // Parse message and create renderable parts
  const messageParts = useMemo(
    () => parseMessage(entry.message, entry.entities),
    [entry.message, entry.entities]
  );

  const renderMessage = () => {
    // Message riche (segments) : prioritaire — libellés déjà humains, liens prêts.
    if (entry.segments && entry.segments.length > 0) {
      return (
        <ActivitySegments
          segments={entry.segments}
          riskId={entry.riskId ?? undefined}
          borrowerProjectId={entry.context?.borrowerProjectId ?? undefined}
        />
      );
    }
    // Fallback : ancien parsing du message brut {EntityType:Guid}.
    return messageParts.map((part, index) => {
      if (part.type === 'entity' && part.entityType && part.entityId) {
        const displayText = getEntityLabel(
          entry.entities,
          part.entityType,
          part.entityId
        );
        return (
          <EntityLink
            key={`${part.entityId}-${index}`}
            entityType={part.entityType}
            entityId={part.entityId}
            displayText={displayText}
            riskId={entry.riskId ?? undefined}
            entities={entry.entities}
          />
        );
      }

      // For text parts, check if it contains the name to make it bold
      if (part.type === 'text' && name && part.content.includes(name)) {
        const textParts = part.content.split(name);
        return (
          <React.Fragment key={index}>
            {textParts[0]}
            <span className="font-semibold">{name}</span>
            {textParts.slice(1).join(name)}
          </React.Fragment>
        );
      }

      return <span key={index}>{part.content}</span>;
    });
  };

  return (
    <div
      onClick={rowUrl ? () => navigate(rowUrl) : undefined}
      className={`flex items-center gap-4 px-5 py-4 transition-colors duration-150 bg-white hover:bg-gray-50 ${
        rowUrl ? 'cursor-pointer' : ''
      }`}
    >
      {/* Avatar */}
      <div
        className={`
          flex-shrink-0 w-11 h-11 rounded-xl
          flex items-center justify-center
          ${avatarColor}
        `}
      >
        <span className="text-white font-semibold text-sm">{initials}</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 leading-snug">{renderMessage()}</p>
        {/* Ligne méta unique : chips de contexte (dédupliqués vs message) + date + courtier */}
        <ActivityContextChips
          context={entry.context}
          segments={entry.segments}
          after={
            <>
              {relativeTime}
              {/* Vue partagée : rattachement courtier (présent uniquement si le back l'envoie) */}
              {entry.brokerName && <> · {entry.brokerName}</>}
            </>
          }
        />
      </div>

      {/* Catégorie (« c'est quoi ») : chip stable par code */}
      {entry.categoryLabel && (
        <span
          className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
            CATEGORY_CHIP[entry.category ?? ''] ?? 'bg-gray-100 text-gray-500'
          }`}
        >
          {entry.categoryLabel}
        </span>
      )}

      {/* Badge */}
      {/* {badge && ( */}
      {/*   <div */}
      {/*     className={` */}
      {/*       flex-shrink-0 h-7 px-3 rounded-full */}
      {/*       flex items-center justify-center */}
      {/*       text-xs font-medium */}
      {/*       ${badge.bgColor} ${badge.textColor} */}
      {/*     `} */}
      {/*   > */}
      {/*     {badge.label} */}
      {/*   </div> */}
      {/* )} */}
    </div>
  );
};

// ============================================
// ACTIVITY FEED SKELETON
// ============================================

const ActivityItemSkeleton: React.FC = () => {
  return (
    <div className="flex items-center gap-4 px-5 py-4 animate-pulse">
      <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-200" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/4" />
      </div>
      <div className="flex-shrink-0 h-7 w-20 rounded-full bg-gray-200" />
    </div>
  );
};

// ============================================
// EMPTY STATE
// ============================================

const EmptyState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      </div>
      <p className="text-gray-500 text-sm">Aucune activite pour ce filtre</p>
    </div>
  );
};

// ============================================
// MAIN ACTIVITY FEED COMPONENT
// ============================================

interface ActivityFeedProps {
  maxItems?: number;
  showViewAll?: boolean;
  showHeader?: boolean;
  showFilters?: boolean;
  showCard?: boolean;
  title?: string;
  searchFilters?: ActivityFeedSearchFilters;
  onViewAll?: () => void;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  maxItems = 10,
  showViewAll = true,
  showCard = true,
  title = "Flux d'activite",
  searchFilters,
  onViewAll
}) => {
  // Activer l'infinite scroll quand showViewAll est false
  const enableInfiniteScroll = !showViewAll;

  const {
    entries,
    isLoading,
    isLoadingMore,
    loadMore,
    hasMore
  } = useActivityFeed({ searchFilters, enableInfiniteScroll }, maxItems);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!enableInfiniteScroll) return;

    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // pixels avant la fin pour déclencher le chargement

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      loadMore();
    }
  }, [enableInfiniteScroll, isLoadingMore, hasMore, loadMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && enableInfiniteScroll) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, enableInfiniteScroll]);

  return (
    <div className="w-full">
      {/* Card Container */}
      <div className={showCard ? "bg-white rounded-2xl border border-gray-200 overflow-hidden" : "overflow-hidden"}>
        {/* List */}
        {isLoading ? (
          <div className="divide-y divide-gray-200">
            {[...Array(3)].map((_, i) => (
              <ActivityItemSkeleton key={i} />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <EmptyState />
        ) : (
          <div
            ref={scrollContainerRef}
            className={`divide-y divide-gray-200 ${enableInfiniteScroll ? 'max-h-[70vh] overflow-y-auto' : ''}`}
          >
            {entries.map((entry) => (
              <ActivityItem
                key={entry.id}
                entry={entry}
              />
            ))}

            {/* Loader infinite scroll */}
            {isLoadingMore && (
              <ActivityItemSkeleton />
            )}

            {/* Pagination : bouton explicite (le scroll infini reste actif en plus) */}
            {enableInfiniteScroll && hasMore && !isLoadingMore && (
              <div className="py-3 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  className="rounded-lg bg-primary/5 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                >
                  Charger plus d'activités
                </button>
              </div>
            )}

            {/* Indicateur fin de liste */}
            {enableInfiniteScroll && !hasMore && entries.length > 0 && (
              <div className="py-4 text-center">
                <span className="text-xs text-gray-400">
                  Fin du flux d'activité
                </span>
              </div>
            )}
          </div>
        )}

        {/* View All Link */}
        {showViewAll && entries.length > 0 && (
          <div className="border-t border-gray-200">
            <button
              onClick={onViewAll}
              className="
                w-full py-4 text-center
                text-sm font-medium text-gray-700
                hover:text-gray-900 hover:bg-gray-50
                transition-colors duration-150
              "
            >
              Tout voir
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
